import { call, put, takeLatest } from 'redux-saga/effects';
import * as types from './LoginActionType';
import * as actions from './LoginActions';
import * as authService from './LoginServices';
import { deleteCookie, setCookie } from '../../../store/localStorage';
import { UPDATE_COMMON_REDUCER } from '../../common/services/CommonActionType';
import { NO_ROLES_MAPPED } from '../../../constants/messageConstants';

function* loginSaga(action) {
    try {
        const response = yield call(authService.login, action.payload);
        if (response && response.status === false) {
            yield put(actions.loginFailure({ 
                message: response.responseMessage || response.message || 'Login failed.',
                type: 'warning' 
            }));
        } else {
            // Check if rolesList is empty
            const rolesList = response.data?.rolesList?.items || [];
            if (rolesList.length === 0) {
                yield put(actions.loginFailure({ 
                    message: NO_ROLES_MAPPED,
                    type: 'warning' 
                }));
                return;
            }
            
            // Check if MFA is required
            const needsMfaSetup = response.data?.requiresMfaSetup === true;
            const needsMfaVerify = response.data?.requiresMfa === true;
            
            if (needsMfaSetup || needsMfaVerify) {
                // Pass the FULL response to component for MFA handling
                yield put(actions.loginSuccess(response));
                return;
            }
            // Normal login flow - no MFA required
            if (response.data && response.data.token) {
                yield call(setCookie, 'authToken', response.data.token);
                yield call(setCookie, 'tokenExpiry', response.data.tokenExpiry);
                yield call(setCookie, 'userData', response.data);
                yield put(actions.loginSuccess(response.data));

                const roles = response.data?.rolesList?.items;
                if (roles && roles.length > 0) {
                    const defaultRole = roles[0];
                    setCookie('selectedRole', defaultRole);
                    yield put({
                    type: UPDATE_COMMON_REDUCER,
                    payload: { selectedRole: defaultRole },
                    });
                }
            } else {
                yield put(actions.loginFailure({ 
                    message: 'Login failed - no token received.',
                    type: 'error' 
                }));
            }

        }
    } catch (error) {
        let msg = error?.response?.data?.responseMessage || error?.response?.data?.message || error.message || 'Login failed.';
        yield put(actions.loginFailure({ 
            message: msg,
            type: 'error' 
        }));
    }
}

function* logoutSaga(action) {
    try {
        yield call(deleteCookie, 'jwtExpirationTime');
        yield call(deleteCookie, 'authToken');
        yield call(deleteCookie, 'tokenExpiry');
        yield call(deleteCookie, 'userData');
        yield put(actions.logoutSuccess(action.payload));
    } catch (error) {
        yield put(actions.logoutFailure(error));
    }
}

function* resetPasswordSaga(action) {
    try {
        const response = yield call(authService.resetPassword, action.payload);
        if (response && response.status === false) {
            yield put(actions.resetPasswordFailure({ message: response.responseMessage || response.message || 'Password reset failed.' }));
        } else {
            yield put(actions.resetPasswordSuccess(response));
        }
    } catch (error) {
        // 4xx responses (e.g. incorrect current password, SSO/Okta account) carry the reason in responseMessage
        let msg = error?.response?.data?.responseMessage || error?.response?.data?.message || error.message || 'Password reset failed.';
        yield put(actions.resetPasswordFailure({ message: msg }));
    }
}

function* forgetPasswordSaga(action) {
    try {
        const response = yield call(authService.forgetPassword, action.payload);
        if (response && response.status === false) {
            yield put(actions.forgetPasswordFailure({ message: response.responseMessage || response.message || 'Request failed.' }));
        } else {
            yield put(actions.forgetPasswordSuccess(response));
        }
    } catch (error) {
        let msg = error?.response?.data?.message || error.message || 'Request failed.';
        yield put(actions.forgetPasswordFailure({ message: msg }));
    }
}

function* forgetPasswordResetSaga(action) {
    try {
        const response = yield call(authService.forgetPasswordReset, action.payload);
        if (response && response.status === false) {
            yield put(actions.forgetPasswordResetFailure({ message: response.responseMessage || response.message || 'Password reset failed.' }));
        } else {
            yield put(actions.forgetPasswordResetSuccess(response));
        }
    } catch (error) {
        let msg = error?.response?.data?.responseMessage || error?.response?.data?.message || error.message || 'Password reset failed.';
        yield put(actions.forgetPasswordResetFailure({ message: msg }));
    }
}

function* verifyMfaSaga(action) {
    try {
        const response = yield call(authService.verifyMfa, action.payload);
        if (response && response.status === false) {
            yield put(actions.verifyMfaFailure({ 
                message: response.responseMessage || response.message || 'MFA verification failed.',
                type: 'error' 
            }));
        } else {
            yield put(actions.verifyMfaSuccess(response));
        }
    } catch (error) {
        let msg = error?.response?.data?.responseMessage || error?.response?.data?.message || error.message || 'MFA verification failed.';
        yield put(actions.verifyMfaFailure({ 
            message: msg,
            type: 'error' 
        }));
    }
}

export default function* watchAuthSaga() {
    yield takeLatest(types.LOGIN_REQUEST, loginSaga);
    yield takeLatest(types.LOGOUT_REQUEST, logoutSaga);
    yield takeLatest(types.RESET_PASSWORD_REQUEST, resetPasswordSaga);
    yield takeLatest(types.FORGET_PASSWORD_REQUEST, forgetPasswordSaga);
    yield takeLatest(types.FORGET_PASSWORD_RESET_REQUEST, forgetPasswordResetSaga);
    yield takeLatest(types.VERIFY_MFA_REQUEST, verifyMfaSaga);
}